import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CanvasContent } from '../types/cockpit';

// Extended content types for the global canvas
export interface GlobalCanvasTab {
  id: string;
  title: string;
  type: 'task' | 'email' | 'file' | 'person' | 'opportunity' | 'chat' | 'canvas' | 'default' | 'clients-list' | 'deals-list' | 'tasks-list' | 'emails-list';
  content: any; // The actual data for the content
  isActive?: boolean;
  icon?: string;
  canClose?: boolean;
  viewId?: string; // Which split view this tab belongs to
  isPinned?: boolean;
  order?: number; // Tab order within view
}

export interface SplitView {
  id: string;
  tabs: GlobalCanvasTab[];
  activeTabId: string | null;
  direction?: 'horizontal' | 'vertical';
  size?: number; // Percentage of available space
}

export interface GlobalCanvasContextType {
  // Tab management
  tabs: GlobalCanvasTab[];
  activeTabId: string | null;
  
  // Split view management
  splitViews: SplitView[];
  activeSplitView: string;
  
  // Tab actions
  openTab: (tab: Omit<GlobalCanvasTab, 'id' | 'isActive'>) => void;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  duplicateTab: (tabId: string) => void;
  popOutTab: (tabId: string) => void;
  
  // Enhanced drag & drop support
  reorderTabs: (viewId: string, oldIndex: number, newIndex: number) => void;
  moveTabBetweenViews: (tabId: string, sourceViewId: string, targetViewId: string, targetIndex?: number) => void;
  
  // Split view actions
  createSplitView: (direction: 'horizontal' | 'vertical', tabId?: string) => string;
  closeSplitView: (viewId: string) => void;
  moveTabToView: (tabId: string, targetViewId: string) => void;
  splitTabToNewView: (tabId: string, direction: 'horizontal' | 'vertical') => void;
  resizeSplitView: (viewId: string, size: number) => void;
  
  // Canvas management
  createCanvas: (title?: string) => void;
  
  // Content-specific openers
  openTask: (task: any) => void;
  openEmail: (email: any) => void;
  openFile: (file: any) => void;
  openPerson: (person: any) => void;
  openOpportunity: (opportunity: any) => void;
  openChat: (chatId?: string) => void;
  
  // Legacy support
  openContent: (content: CanvasContent) => void;
}

const GlobalCanvasContext = createContext<GlobalCanvasContextType | undefined>(undefined);

interface GlobalCanvasProviderProps {
  children: ReactNode;
}

export const GlobalCanvasProvider: React.FC<GlobalCanvasProviderProps> = ({ children }) => {
  const [tabs, setTabs] = useState<GlobalCanvasTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [splitViews, setSplitViews] = useState<SplitView[]>([{
    id: 'main',
    tabs: [],
    activeTabId: null,
    size: 100
  }]);
  const [activeSplitView, setActiveSplitView] = useState<string>('main');

  // Generate unique tab ID
  const generateTabId = useCallback(() => {
    return `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Check if a tab already exists for the same content
  const findExistingTab = useCallback((type: string, contentId: string) => {
    return tabs.find(tab =>
      tab.type === type &&
      (tab.content?.id === contentId || tab.content?.ID === contentId || tab.content?.Id === contentId ||
       tab.content?.mercuryId === contentId)
    );
  }, [tabs]);

  // Enhanced helper function to get contextual icons - CONSISTENT WITH SIDEBAR
  const getIconForContentType = useCallback((type: string, content?: any): string => {
    switch (type) {
      case 'task': 
        // Different icons based on task status/importance - using Material Symbols
        if (content?.isCompleted) return 'task_alt';
        if (content?.importance === 'high' || content?.priority?.includes('high')) return 'priority_high';
        return 'task_alt';
      
      case 'email':
        // Different icons based on email properties - using Material Symbols
        if (content?.hasAttachments) return 'attach_file';
        if (content?.isRead === false) return 'mark_email_unread';
        if (content?.importance === 'high') return 'priority_high';
        return 'mail';
      
      case 'file':
        // File type specific icons - using Material Symbols
        const fileName = content?.name || '';
        const extension = fileName.split('.').pop()?.toLowerCase();
        if (['pdf'].includes(extension)) return 'picture_as_pdf';
        if (['doc', 'docx'].includes(extension)) return 'article';
        if (['xls', 'xlsx'].includes(extension)) return 'table_chart';
        if (['ppt', 'pptx'].includes(extension)) return 'slideshow';
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) return 'image';
        if (['txt', 'md', 'json', 'xml', 'csv', 'log', 'yml', 'yaml'].includes(extension)) return 'edit_document';
        if (['zip', 'rar', '7z', 'tar'].includes(extension)) return 'archive';
        return 'description';
      
      case 'person':
        return 'person';
        
      case 'opportunity':
        // Different icons based on opportunity stage/value - using Material Symbols  
        if (content?.stage?.includes('closed') || content?.status?.includes('won')) return 'monetization_on';
        if (content?.stage?.includes('proposal') || content?.stage?.includes('negotiation')) return 'handshake';
        return 'monetization_on'; // Same as deals in sidebar
      
      case 'chat':
        // Different icons based on chat type or participants - using Material Symbols
        if (content?.isGroup) return 'forum';
        if (content?.participants?.length > 2) return 'groups';
        return 'chat';
        
      case 'canvas':
        return 'draw';
        
      default: 
        return 'description';
    }
  }, []);

  // Open a new tab or switch to existing one
  const openTab = useCallback((tabData: Omit<GlobalCanvasTab, 'id' | 'isActive'>) => {
    // Debug logging only in development
    if (process.env.NODE_ENV === 'development') {
      console.debug('🎯 Opening new tab:', { 
        title: tabData.title, 
        type: tabData.type,
        content: tabData.content 
      });
    }
    
    const contentId = tabData.content?.id || tabData.content?.ID || tabData.content?.Id || tabData.title;
    const existingTab = findExistingTab(tabData.type, contentId);
    
    if (existingTab) {
      // Switch to existing tab - update both global and split view active tab
      if (process.env.NODE_ENV === 'development') {
        console.debug('🔄 Switching to existing tab:', existingTab.title);
      }
      setActiveTabId(existingTab.id);
      setSplitViews(prevViews =>
        prevViews.map(view =>
          view.tabs.some(tab => tab.id === existingTab.id)
            ? { ...view, activeTabId: existingTab.id }
            : view
        )
      );
      return;
    }

    // Create new tab
    const newTabId = generateTabId();
    const newTab: GlobalCanvasTab = {
      ...tabData,
      id: newTabId,
      isActive: true,
      canClose: true,
      viewId: activeSplitView
    };

    if (process.env.NODE_ENV === 'development') {
      console.debug('✨ Creating new tab:', newTab);
    }
    
    // Add tab to global tabs array
    setTabs(prevTabs => {
      if (process.env.NODE_ENV === 'development') {
        console.debug('📋 Previous tabs:', prevTabs.length);
      }
      const newTabs = [...prevTabs, newTab];
      if (process.env.NODE_ENV === 'development') {
        console.debug('📋 New tabs:', newTabs.length);
      }
      return newTabs;
    });
    
    // Add tab to the active split view
    setSplitViews(prevViews => 
      prevViews.map(view => 
        view.id === activeSplitView 
          ? { ...view, tabs: [...view.tabs, newTab], activeTabId: newTabId }
          : view
      )
    );
    
    setActiveTabId(newTabId);
  }, [findExistingTab, generateTabId, activeSplitView]);

  // Close a tab
  const closeTab = useCallback((tabId: string) => {
    // Remove from global tabs
    setTabs(prevTabs => {
      const updatedTabs = prevTabs.filter(tab => tab.id !== tabId);
      
      // If we closed the active tab, switch to the last tab
      if (tabId === activeTabId) {
        if (updatedTabs.length > 0) {
          setActiveTabId(updatedTabs[updatedTabs.length - 1].id);
        } else {
          setActiveTabId(null);
        }
      }
      
      return updatedTabs;
    });
    
    // Remove from split views and track empty views to close
    setSplitViews(prevViews => {
      const updatedViews = prevViews.map(view => {
        const newTabs = view.tabs.filter(tab => tab.id !== tabId);
        return {
          ...view,
          tabs: newTabs,
          activeTabId: view.activeTabId === tabId ? 
            (newTabs.length > 0 ? newTabs[0]?.id || null : null) : 
            view.activeTabId
        };
      });

      // Find empty views (excluding 'main' view) and close them
      const emptyViewIds = updatedViews
        .filter(view => view.id !== 'main' && view.tabs.length === 0)
        .map(view => view.id);

      // Remove empty split views
      let finalViews = updatedViews.filter(view => view.id === 'main' || view.tabs.length > 0);

      // If we removed views, redistribute the space
      if (emptyViewIds.length > 0 && finalViews.length > 1) {
        const totalRemovedSpace = emptyViewIds.reduce((total, viewId) => {
          const view = updatedViews.find(v => v.id === viewId);
          return total + (view?.size || 0);
        }, 0);
        
        const nonMainViews = finalViews.filter(v => v.id !== 'main');
        if (nonMainViews.length > 0) {
          const spacePerView = totalRemovedSpace / nonMainViews.length;
          finalViews = finalViews.map(view => 
            view.id === 'main' ? view : {
              ...view,
              size: (view.size || 50) + spacePerView
            }
          );
        }
      }

      // If the active split view was closed, switch to main
      if (emptyViewIds.includes(activeSplitView)) {
        setActiveSplitView('main');
      }

      return finalViews;
    });
  }, [activeTabId, activeSplitView]);

  // Set active tab
  const setActiveTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
    
    // Update the split view's active tab
    setSplitViews(prevViews => 
      prevViews.map(view => 
        view.tabs.some(tab => tab.id === tabId) 
          ? { ...view, activeTabId: tabId }
          : view
      )
    );
    
    // Set the active split view to the one containing this tab
    const targetView = splitViews.find(view => view.tabs.some(tab => tab.id === tabId));
    if (targetView) {
      setActiveSplitView(targetView.id);
    }
  }, [splitViews]);

  // Duplicate a tab
  const duplicateTab = useCallback((tabId: string) => {
    const tabToDuplicate = tabs.find(tab => tab.id === tabId);
    if (tabToDuplicate) {
      const newTab = {
        ...tabToDuplicate,
        title: `${tabToDuplicate.title} (Copy)`,
        canClose: true
      };
      delete (newTab as any).id;
      delete (newTab as any).isActive;
      openTab(newTab);
    }
  }, [tabs, openTab]);

  // Pop out tab to new window
  const popOutTab = useCallback((tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      // Use the enhanced popup window system
      import('../components/PopOutWindow').then(({ createPopupWindowManager }) => {
        const manager = createPopupWindowManager();
        const popup = manager.createPopupWindow(tab);
        
        if (popup) {
          console.log(`🪟 Tab "${tab.title}" popped out to new window`);
          // Optionally close the original tab or keep it
          // closeTab(tabId);
        } else {
          console.error('Failed to create popup window - popup blocker may be active');
        }
      });
    }
  }, [tabs]);

  // Content-specific opening functions
  const openTask = useCallback((task: any) => {
    console.log('📋 openTask called with:', { 
      task: task.task || task.title || task.subject, 
      id: task.id 
    });
    openTab({
      title: task.task || task.title || task.subject || 'Task',
      type: 'task',
      content: task,
      icon: getIconForContentType('task', task)
    });
  }, [openTab, getIconForContentType]);

  const openEmail = useCallback((email: any) => {
    console.log('📧 openEmail called with:', { 
      subject: email.subject, 
      id: email.id 
    });
    openTab({
      title: email.subject || 'Email',
      type: 'email', 
      content: email,
      icon: getIconForContentType('email', email)
    });
  }, [openTab, getIconForContentType]);

  const openFile = useCallback((file: any) => {
    openTab({
      title: file.name || 'File',
      type: 'file',
      content: file,
      icon: getIconForContentType('file', file)
    });
  }, [openTab, getIconForContentType]);

  const openPerson = useCallback((person: any) => {
    openTab({
      title: person.name || person.displayName || 'Person',
      type: 'person',
      content: person,
      icon: getIconForContentType('person', person)
    });
  }, [openTab, getIconForContentType]);

  const openOpportunity = useCallback((opportunity: any) => {
    openTab({
      title: opportunity.name || opportunity.title || 'Opportunity',
      type: 'opportunity',
      content: opportunity,
      icon: getIconForContentType('opportunity', opportunity)
    });
  }, [openTab, getIconForContentType]);

  const openChat = useCallback((chatId?: string) => {
    const chatContent = { chatId };
    openTab({
      title: `Chat ${chatId || new Date().toLocaleTimeString()}`,
      type: 'chat',
      content: chatContent,
      icon: getIconForContentType('chat', chatContent)
    });
  }, [openTab, getIconForContentType]);

  // Legacy support for existing CanvasContent
  const openContent = useCallback((content: CanvasContent) => {
    openTab({
      title: content.title || 'Content',
      type: content.type as any || 'default',
      content: content.data || content,
      icon: getIconForContentType(content.type, content.data || content)
    });
  }, [openTab, getIconForContentType]);

  // Split view management functions
  const createSplitView = useCallback((direction: 'horizontal' | 'vertical', tabId?: string) => {
    const newViewId = `view-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    setSplitViews(prevViews => {
      const newView: SplitView = {
        id: newViewId,
        tabs: [],
        activeTabId: null,
        direction,
        size: 50 // Split 50/50 by default
      };
      
      // If moving an existing tab, move it to the new view
      if (tabId) {
        const existingTab = tabs.find(tab => tab.id === tabId);
        if (existingTab) {
          newView.tabs = [{ ...existingTab, viewId: newViewId }];
          newView.activeTabId = tabId;
        }
      }
      
      return [...prevViews, newView];
    });
    
    setActiveSplitView(newViewId);
    return newViewId;
  }, [tabs]);

  const closeSplitView = useCallback((viewId: string) => {
    if (viewId === 'main') return; // Can't close main view
    
    setSplitViews(prevViews => {
      const viewToClose = prevViews.find(v => v.id === viewId);
      if (!viewToClose) return prevViews;
      
      // Move tabs from closing view to main view
      if (viewToClose.tabs.length > 0) {
        setTabs(prevTabs => [
          ...prevTabs,
          ...viewToClose.tabs.map(tab => ({ ...tab, viewId: 'main' }))
        ]);
      }
      
      const remainingViews = prevViews.filter(v => v.id !== viewId);
      
      // Redistribute space among remaining views
      if (remainingViews.length > 1) {
        const redistributedSpace = viewToClose.size! / (remainingViews.length - 1);
        return remainingViews.map(v => v.id === 'main' ? v : {
          ...v,
          size: (v.size || 50) + redistributedSpace
        });
      }
      
      return remainingViews;
    });
    
    // Switch to main view if closing active view
    if (viewId === activeSplitView) {
      setActiveSplitView('main');
    }
  }, [activeSplitView]);

  const moveTabToView = useCallback((tabId: string, targetViewId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    setTabs(prevTabs => 
      prevTabs.map(tab => 
        tab.id === tabId ? { ...tab, viewId: targetViewId } : tab
      )
    );
    
    setSplitViews(prevViews => {
      const updatedViews = prevViews.map(view => {
        if (view.id === targetViewId) {
          return {
            ...view,
            tabs: view.tabs.some(t => t.id === tabId) 
              ? view.tabs 
              : [...view.tabs, { ...tab, viewId: targetViewId }],
            activeTabId: tabId
          };
        } else {
          const newTabs = view.tabs.filter(t => t.id !== tabId);
          return {
            ...view,
            tabs: newTabs,
            activeTabId: view.activeTabId === tabId ? (newTabs.length > 0 ? newTabs[0]?.id : null) : view.activeTabId
          };
        }
      });

      // Remove any empty views (except 'main')
      const finalViews = updatedViews.filter(view => view.id === 'main' || view.tabs.length > 0);

      // Redistribute space if views were removed
      if (finalViews.length < updatedViews.length && finalViews.length > 1) {
        const removedViews = updatedViews.filter(view => view.id !== 'main' && view.tabs.length === 0);
        const totalRemovedSpace = removedViews.reduce((total, view) => total + (view.size || 0), 0);
        
        if (totalRemovedSpace > 0) {
          const nonMainViews = finalViews.filter(v => v.id !== 'main');
          if (nonMainViews.length > 0) {
            const spacePerView = totalRemovedSpace / nonMainViews.length;
            return finalViews.map(view => 
              view.id === 'main' ? view : {
                ...view,
                size: (view.size || 50) + spacePerView
              }
            );
          }
        }
      }

      return finalViews;
    });
    
    setActiveSplitView(targetViewId);
  }, [tabs]);

  const splitTabToNewView = useCallback((tabId: string, direction: 'horizontal' | 'vertical') => {
    const newViewId = createSplitView(direction, tabId);
    moveTabToView(tabId, newViewId);
  }, [createSplitView, moveTabToView]);

  // Enhanced drag & drop methods
  const reorderTabs = useCallback((viewId: string, oldIndex: number, newIndex: number) => {
    setSplitViews(prevViews => 
      prevViews.map(view => {
        if (view.id === viewId) {
          const newTabs = [...view.tabs];
          const [movedTab] = newTabs.splice(oldIndex, 1);
          newTabs.splice(newIndex, 0, movedTab);
          return { ...view, tabs: newTabs };
        }
        return view;
      })
    );
  }, []);

  const moveTabBetweenViews = useCallback((tabId: string, sourceViewId: string, targetViewId: string, targetIndex?: number) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    // Remove from source view and add to target view
    setSplitViews(prevViews => {
      const updatedViews = prevViews.map(view => {
        if (view.id === sourceViewId) {
          const newTabs = view.tabs.filter(t => t.id !== tabId);
          return {
            ...view,
            tabs: newTabs,
            activeTabId: view.activeTabId === tabId ? (newTabs.length > 0 ? newTabs[0]?.id : null) : view.activeTabId
          };
        } else if (view.id === targetViewId) {
          const newTabs = [...view.tabs];
          if (typeof targetIndex === 'number') {
            newTabs.splice(targetIndex, 0, { ...tab, viewId: targetViewId });
          } else {
            newTabs.push({ ...tab, viewId: targetViewId });
          }
          return {
            ...view,
            tabs: newTabs,
            activeTabId: tabId
          };
        }
        return view;
      });

      // Check if source view became empty (and is not 'main') and close it
      const sourceView = updatedViews.find(v => v.id === sourceViewId);
      if (sourceView && sourceViewId !== 'main' && sourceView.tabs.length === 0) {
        // Remove the empty source view
        const finalViews = updatedViews.filter(v => v.id !== sourceViewId);
        
        // Redistribute space if needed
        if (finalViews.length > 1) {
          const removedSpace = sourceView.size || 50;
          const nonMainViews = finalViews.filter(v => v.id !== 'main');
          if (nonMainViews.length > 0) {
            const spacePerView = removedSpace / nonMainViews.length;
            return finalViews.map(view => 
              view.id === 'main' ? view : {
                ...view,
                size: (view.size || 50) + spacePerView
              }
            );
          }
        }
        
        return finalViews;
      }

      return updatedViews;
    });

    // Update the tab's viewId in the global tabs array
    setTabs(prevTabs => 
      prevTabs.map(t => t.id === tabId ? { ...t, viewId: targetViewId } : t)
    );
    
    setActiveSplitView(targetViewId);
    setActiveTabId(tabId);
  }, [tabs]);

  const resizeSplitView = useCallback((viewId: string, size: number) => {
    setSplitViews(prevViews => 
      prevViews.map(view => 
        view.id === viewId ? { ...view, size } : view
      )
    );
  }, []);

  const createCanvas = useCallback((title?: string) => {
    const canvasTitle = title || `Canvas ${new Date().toLocaleTimeString()}`;
    const canvasContent = {
      id: `canvas-${Date.now()}`,
      nodes: [],
      connections: []
    };

    console.log('🎨 Creating canvas:', canvasTitle);
    
    openTab({
      title: canvasTitle,
      type: 'canvas',
      content: canvasContent,
      icon: 'draw',
      canClose: true
    });
  }, [openTab]);

  const contextValue: GlobalCanvasContextType = {
    tabs,
    activeTabId,
    splitViews,
    activeSplitView,
    openTab,
    closeTab,
    setActiveTab,
    duplicateTab,
    popOutTab,
    reorderTabs,
    moveTabBetweenViews,
    createSplitView,
    closeSplitView,
    moveTabToView,
    splitTabToNewView,
    resizeSplitView,
    createCanvas,
    openTask,
    openEmail,
    openFile,
    openPerson,
    openOpportunity,
    openChat,
    openContent
  };

  return (
    <GlobalCanvasContext.Provider value={contextValue}>
      {children}
    </GlobalCanvasContext.Provider>
  );
};

// Hook to use the global canvas context
export const useGlobalCanvas = (): GlobalCanvasContextType => {
  const context = useContext(GlobalCanvasContext);
  if (!context) {
    throw new Error('useGlobalCanvas must be used within a GlobalCanvasProvider');
  }
  return context;
};

// Hook for easy content opening (can be used anywhere in the app)
export const useOpenInCanvas = () => {
  const { openTask, openEmail, openFile, openPerson, openOpportunity, openChat, openTab } = useGlobalCanvas();
  
  return {
    openTask,
    openEmail, 
    openFile,
    openPerson,
    openOpportunity,
    openChat,
    openTab
  };
};
